import { apiFetch } from "./api";
import type { Paginated } from "./platform-api";

export type SiteStatus = "ACTIVE" | "INACTIVE";
export type WfhPolicy = "INHERIT" | "ALLOW" | "DENY";
export type GeofenceMode = "GPS_AND_GEOFENCE" | "GPS_ONLY" | "MANUAL";

export interface SiteSummary {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  /** `null` = inherit the organisation's default radius. */
  geofenceRadiusM: number | null;
  geofenceMode: GeofenceMode;
  /** WFH override for employees whose primary site is this one. */
  wfhPolicy: WfhPolicy;
  /** Departments operating at this site (M-N, ADR-0008). */
  departments: { id: string; name: string }[];
  status: SiteStatus;
  /**
   * Optional per-site attendance overrides. `null` means "inherit tenant default".
   * Admins only need to set these if a site runs different hours from the org.
   */
  /** Per-site working week, ISO 1=Mon..7=Sun. EMPTY = inherit the tenant's. */
  workingDays: number[];
  /** Informational label only (BR-203); no rule reads it. */
  state: string | null;
  workStartTime: string | null;
  workEndTime: string | null;
  gracePeriodMins: number | null;
  lateThresholdMins: number | null;
  minWorkHoursPerDay: number | null;
  assignedEmployees: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListSitesQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: SiteStatus;
  geofenceMode?: GeofenceMode;
  wfhPolicy?: WfhPolicy;
  /** Managed department ids (replace-set on update). */
  departmentIds?: string[];
}

export interface CreateSiteBody {
  code: string;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  /** Omit or send `null` to inherit the organisation's default. */
  geofenceRadiusM?: number | null;
  geofenceMode?: GeofenceMode;
  wfhPolicy?: WfhPolicy;
  /** Managed department ids (replace-set on update). */
  departmentIds?: string[];
  status?: SiteStatus;
  // Optional overrides. Omit or send `null` to clear.
  workingDays?: number[];
  state?: string | null;
  workStartTime?: string | null;
  workEndTime?: string | null;
  gracePeriodMins?: number | null;
  lateThresholdMins?: number | null;
  minWorkHoursPerDay?: number | null;
}

export type UpdateSiteBody = Partial<CreateSiteBody>;

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const sitesApi = {
  list: (q: ListSitesQuery = {}) =>
    apiFetch<Paginated<SiteSummary>>(`/sites${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<SiteSummary>(`/sites/${id}`),

  create: (body: CreateSiteBody) =>
    apiFetch<SiteSummary>(`/sites`, { method: "POST", body }),

  update: (id: string, body: UpdateSiteBody) =>
    apiFetch<SiteSummary>(`/sites/${id}`, { method: "PATCH", body }),

  remove: (id: string) =>
    apiFetch<{ id: string; deleted: boolean }>(`/sites/${id}`, { method: "DELETE" }),
};
